/**
 * The two editing dialogs that sit beside the grid (Workstream, Plan Line). The plan's
 * Changes tab lives in `PlanChangesPanel.tsx` (RP-25).
 *
 * The Estimates tab lives in `EstimatesPanel.tsx` — see that file's header for why a
 * plan's estimate is a manually-arranged outline of Estimated Hours Lines, never the grid
 * (docs/specs/resource-plan.md §11, revised 2026-09-29).
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useLaborCategories } from "@/hooks/use-rate-card";
import {
  useCreatePlanLine,
  useCreateWorkstream,
  useDeletePlanLine,
  useDeleteWorkstream,
  useUpdatePlanLine,
  useUpdateWorkstream,
} from "@/hooks/use-resource-plans";
import { formatPlanMoney } from "@/lib/plan-format";
import { disciplinesFromPlannedPod, positionLabel } from "@/lib/position-label";
import type {
  PlanLineView,
  ResourcePlanDetailView,
  WorkstreamView,
} from "@/schemas/resource-plans";

const DECIMAL2 = /^\d+(\.\d{1,2})?$/;
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

// ── Workstream dialog (RP-3.1) ────────────────────────────────────────────────

const WORKSTREAM_TYPES = [
  ["Experience", "Experience"],
  ["Workflow", "Workflow"],
  ["Integration", "Integration"],
  ["Foundation", "Foundation"],
  ["Advisory", "Advisory"],
  ["RunSupport", "Run / Support"],
] as const;

const workstreamSchema = z.object({
  name: z.string().trim().min(1, { message: "Give the Workstream a name" }).max(120),
  type: z.enum(["Experience", "Workflow", "Integration", "Foundation", "Advisory", "RunSupport"]),
  description: z.string().trim().max(2000).optional(),
  startMonth: z.string().regex(MONTH).or(z.literal("")).optional(),
  endMonth: z.string().regex(MONTH).or(z.literal("")).optional(),
});

export function WorkstreamDialog(props: {
  planId: string;
  workstream: WorkstreamView | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const isEdit = props.workstream !== null && props.workstream.id !== null;
  const create = useCreateWorkstream(props.planId);
  const update = useUpdateWorkstream();
  const remove = useDeleteWorkstream();
  const pending = create.isPending || update.isPending || remove.isPending;

  const form = useForm<z.infer<typeof workstreamSchema>>({
    resolver: zodResolver(workstreamSchema),
    defaultValues: {
      name: "",
      type: "Experience",
      description: "",
      startMonth: "",
      endMonth: "",
    },
  });

  useEffect(() => {
    if (!props.open) return;
    form.reset({
      name: props.workstream?.name ?? "",
      type: (props.workstream?.type ?? "Experience") as z.infer<typeof workstreamSchema>["type"],
      description: props.workstream?.description ?? "",
      startMonth: props.workstream?.startMonth ?? "",
      endMonth: props.workstream?.endMonth ?? "",
    });
  }, [props.open, props.workstream, form]);

  function onSubmit(values: z.infer<typeof workstreamSchema>) {
    const blank = (v: string | undefined) => (v === undefined || v === "" ? null : v);
    const payload = {
      name: values.name,
      type: values.type,
      description: blank(values.description),
      startMonth: blank(values.startMonth),
      endMonth: blank(values.endMonth),
    };
    if (isEdit && props.workstream?.id !== null && props.workstream?.id !== undefined) {
      update.mutate(
        { id: props.workstream.id, ...payload },
        { onSuccess: () => props.onOpenChange(false) },
      );
      return;
    }
    create.mutate(payload, { onSuccess: () => props.onOpenChange(false) });
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Workstream" : "Add Workstream"}</DialogTitle>
          <DialogDescription>
            A named section of the plan — Plan Lines group and subtotal under it.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Mobile App" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Workstream Type</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {WORKSTREAM_TYPES.map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <p className="text-sm text-muted-foreground">
              Estimated hours are set on the Estimates tab.
            </p>
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Description{" "}
                    <span className="font-normal text-muted-foreground">(optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="gap-2 sm:justify-between">
              {isEdit && props.workstream?.id !== null && props.workstream?.id !== undefined ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={pending}
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Remove "${props.workstream?.name}"? Its Plan Lines and their hours go with it.`,
                      )
                    ) {
                      return;
                    }
                    remove.mutate(props.workstream?.id ?? "", {
                      onSuccess: () => props.onOpenChange(false),
                    });
                  }}
                  className="text-destructive"
                >
                  Remove
                </Button>
              ) : (
                <span />
              )}
              <span className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => props.onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={pending}>
                  {pending ? "Saving…" : isEdit ? "Save Changes" : "Add"}
                </Button>
              </span>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ── Plan Line dialog (RP-4.2, RP-6.2, RP-6.3) ─────────────────────────────────

const POSITIONS = ["R1", "R2", "R3", "R4", "Supporting"] as const;
const NO_POSITION = "__none__";

const lineSchema = z.object({
  laborCategoryId: z.string().min(1, { message: "Pick a Labor Category" }),
  clientLaborCategory: z.string().trim().max(120).optional(),
  position: z.string(),
  billRate: z.string().regex(DECIMAL2).or(z.literal("")).optional(),
  rateOverrideReason: z.string().trim().max(500).optional(),
});

export function PlanLineDialog(props: {
  plan: ResourcePlanDetailView;
  line: PlanLineView | null;
  /** Which Workstream a NEW line belongs to. `null` is plan-level. */
  workstreamId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const isEdit = props.line !== null;
  const create = useCreatePlanLine(props.plan.id);
  const update = useUpdatePlanLine();
  const remove = useDeletePlanLine();
  const { data: categories } = useLaborCategories();
  const pending = create.isPending || update.isPending || remove.isPending;
  // "R1 · SA": what each Pod Position is for, from the plan's own Planned Pod.
  const podDisciplines = disciplinesFromPlannedPod(props.plan.plannedPod?.positions);

  const clientDictated = props.plan.rateMode === "ClientDictated";
  const form = useForm<z.infer<typeof lineSchema>>({
    resolver: zodResolver(lineSchema),
    defaultValues: {
      laborCategoryId: "",
      clientLaborCategory: "",
      position: NO_POSITION,
      billRate: "",
      rateOverrideReason: "",
    },
  });

  useEffect(() => {
    if (!props.open) return;
    form.reset({
      laborCategoryId: props.line?.laborCategoryId ?? "",
      clientLaborCategory: props.line?.clientLaborCategory ?? "",
      position: props.line?.position ?? NO_POSITION,
      // In Card mode an unedited line shows blank so it keeps taking the card rate.
      billRate: props.line !== null && props.line.rateSource !== "Card" ? props.line.billRate : "",
      rateOverrideReason: props.line?.rateOverrideReason ?? "",
    });
  }, [props.open, props.line, form]);

  const watched = form.watch();
  const listRate =
    categories?.find((c) => c.id === watched.laborCategoryId) !== undefined
      ? (props.plan.lines.find((l) => l.laborCategoryId === watched.laborCategoryId)?.listRate ??
        null)
      : null;
  const overriding =
    !clientDictated &&
    (watched.billRate ?? "") !== "" &&
    listRate !== null &&
    watched.billRate !== listRate;

  function onSubmit(values: z.infer<typeof lineSchema>) {
    const blank = (v: string | undefined) => (v === undefined || v === "" ? null : v);
    const payload = {
      laborCategoryId: values.laborCategoryId,
      clientLaborCategory: blank(values.clientLaborCategory),
      position: values.position === NO_POSITION ? null : values.position,
      ...((values.billRate ?? "") === "" ? {} : { billRate: values.billRate }),
      rateOverrideReason: blank(values.rateOverrideReason),
    };
    if (isEdit && props.line !== null) {
      update.mutate(
        {
          id: props.line.id,
          ...payload,
          // Clearing the rate in Card mode means "put it back on the card".
          ...(!clientDictated && (values.billRate ?? "") === "" ? { resetRateToCard: true } : {}),
        },
        { onSuccess: () => props.onOpenChange(false) },
      );
      return;
    }
    create.mutate(
      { workstreamId: props.workstreamId, ...payload },
      { onSuccess: () => props.onOpenChange(false) },
    );
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Plan Line" : "Add Plan Line"}</DialogTitle>
          <DialogDescription>
            A Labor Category priced at a bill rate. Its hours are typed into the grid.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="laborCategoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Labor Category</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Pick one" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {(categories ?? [])
                        .filter((c) => c.active)
                        .map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                            {c.disciplineCode !== null && ` · ${c.disciplineCode}`}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Cost comes from the Labor Category, so every line needs one.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="clientLaborCategory"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Client Label{" "}
                    <span className="font-normal text-muted-foreground">(optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Solution Architect SME"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormDescription>
                    The finer title the client sees. Set one and it REPLACES the Labor Category name
                    in the grid and on the export — the line is still priced as the Labor Category
                    above, which is never renamed.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="billRate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Bill Rate{" "}
                    {!clientDictated && (
                      <span className="font-normal text-muted-foreground">
                        (blank takes the card rate)
                      </span>
                    )}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        $
                      </span>
                      <Input
                        inputMode="decimal"
                        placeholder={listRate ?? "135.63"}
                        {...field}
                        value={field.value ?? ""}
                        className="pl-6"
                      />
                    </div>
                  </FormControl>
                  <FormDescription>
                    {clientDictated
                      ? "This plan uses Client-Dictated rates, so every line carries its own."
                      : listRate !== null
                        ? `The list rate on ${props.plan.rateBasisLabel} is ${formatPlanMoney(listRate)}.`
                        : "Resolves from the Rate Card for this plan's Rate Basis."}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {overriding && (
              <FormField
                control={form.control}
                name="rateOverrideReason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Override reason</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Negotiated concession, RFP schedule"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormDescription>Required — it shows in the plan history.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="position"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Pod Position{" "}
                    <span className="font-normal text-muted-foreground">(optional)</span>
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NO_POSITION}>None</SelectItem>
                      {POSITIONS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {positionLabel(p, podDisciplines)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Not shown in the grid — Handover and the demand rollup use it.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 sm:justify-between">
              {isEdit && props.line !== null ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={pending}
                  onClick={() => {
                    const hasHours = props.line !== null && props.line.totalHours !== "0.00";
                    if (
                      hasHours &&
                      !window.confirm(
                        `This line has ${props.line?.totalHours} hours. Removing it discards them. Continue?`,
                      )
                    ) {
                      return;
                    }
                    remove.mutate(props.line?.id ?? "", {
                      onSuccess: () => props.onOpenChange(false),
                    });
                  }}
                  className="text-destructive"
                >
                  Remove
                </Button>
              ) : (
                <span />
              )}
              <span className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => props.onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={pending}>
                  {pending ? "Saving…" : isEdit ? "Save Changes" : "Add"}
                </Button>
              </span>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
